import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  AlertTriangle, 
  Shield, 
  Calendar, 
  ArrowRight,
  X,
  CheckCircle2,
  Clock
} from 'lucide-react';
import { Button } from './ui/button';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from './ui/dialog';
import { Checkbox } from './ui/checkbox';
import { Label } from './ui/label';
import axios from 'axios';
import { toast } from 'sonner';

import { API_BASE } from '../utils/apiUrl';
const API = API_BASE;

/**
 * BankFeedSafetyModal
 * 
 * Shows warnings when user attempts to import bank transactions
 * before their Financial Anchor Date (FAD).
 * 
 * Helps prevent double-counting with opening balances.
 */
export const BankFeedSafetyModal = ({ 
  open, 
  onClose, 
  onProceed,
  startDate,
  bankName = 'bank account',
  validationResult = null
}) => {
  const [confirmHistoricalMode, setConfirmHistoricalMode] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleProceed = async () => {
    if (validationResult?.requires_confirmation && !confirmHistoricalMode) {
      toast.error('Please confirm you understand the implications');
      return;
    }

    setLoading(true);
    
    try {
      // If enabling history mode, mark the bank account
      if (validationResult?.will_enable_history_mode) {
        // This would need the bank_account_id passed in
        // For now, we just proceed and let the parent handle it
      }
      
      if (onProceed) {
        await onProceed();
      }
      
      onClose();
    } catch (error) {
      console.error('Failed to proceed:', error);
      toast.error('Something went wrong');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-US', { 
      year: 'numeric', 
      month: 'long', 
      day: 'numeric' 
    });
  };

  // If no validation issues and no confirmation needed, don't show modal
  // Show modal if: has warnings, requires confirmation, or can't proceed
  if (!validationResult || (validationResult.can_proceed && !validationResult.requires_confirmation && !validationResult.warnings?.length)) {
    return null;
  }

  const hasHighSeverityWarning = validationResult.warnings?.some(
    w => w.severity === 'high'
  );

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <div className="flex items-center gap-3">
            <div className={`p-2 rounded-lg ${hasHighSeverityWarning ? 'bg-red-100' : 'bg-amber-100'}`}>
              <AlertTriangle className={`w-6 h-6 ${hasHighSeverityWarning ? 'text-red-600' : 'text-amber-600'}`} />
            </div>
            <div>
              <DialogTitle>
                {hasHighSeverityWarning ? 'Double-Counting Risk' : 'Historical Import Warning'}
              </DialogTitle>
              <DialogDescription>
                Review before importing transactions
              </DialogDescription>
            </div>
          </div>
        </DialogHeader>

        <div className="space-y-4 py-4">
          {/* Date Info */}
          <div className="flex items-center gap-3 p-3 bg-muted rounded-lg">
            <Calendar className="w-5 h-5 text-muted-foreground" />
            <div>
              <p className="text-sm font-medium">Import Start Date</p>
              <p className="text-sm text-muted-foreground">{formatDate(startDate)}</p>
            </div>
          </div>

          {/* Warnings */}
          <div className="space-y-3">
            {validationResult.warnings?.map((warning, idx) => (
              <motion.div
                key={idx}
                initial={{ opacity: 0, x: -10 }}
                animate={{ opacity: 1, x: 0 }}
                transition={{ delay: idx * 0.1 }}
                className={`p-4 rounded-lg border-2 ${
                  warning.severity === 'high' 
                    ? 'bg-red-50 border-red-200' 
                    : 'bg-amber-50 border-amber-200'
                }`}
              >
                <div className="flex items-start gap-3">
                  <AlertTriangle className={`w-5 h-5 mt-0.5 ${
                    warning.severity === 'high' ? 'text-red-600' : 'text-amber-600'
                  }`} />
                  <div>
                    <p className={`font-medium ${
                      warning.severity === 'high' ? 'text-red-800' : 'text-amber-800'
                    }`}>
                      {warning.type === 'double_counting_risk' 
                        ? 'Opening Balance Conflict' 
                        : 'Historical Transaction Import'}
                    </p>
                    <p className={`text-sm mt-1 ${
                      warning.severity === 'high' ? 'text-red-700' : 'text-amber-700'
                    }`}>
                      {warning.message}
                    </p>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>

          {/* Recommendations */}
          {validationResult.recommendations?.length > 0 && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="font-medium text-blue-800 flex items-center gap-2">
                <Shield className="w-4 h-4" />
                Recommendations
              </p>
              <ul className="mt-2 space-y-1">
                {validationResult.recommendations.map((rec, idx) => (
                  <li key={idx} className="text-sm text-blue-700 flex items-start gap-2">
                    <CheckCircle2 className="w-4 h-4 mt-0.5 flex-shrink-0" />
                    {rec}
                  </li>
                ))}
              </ul>
            </div>
          )}

          {/* Confirmation checkbox */}
          {validationResult.requires_confirmation && (
            <div className="flex items-start space-x-3 p-4 bg-slate-50 rounded-lg">
              <Checkbox
                id="confirm-historical"
                checked={confirmHistoricalMode}
                onCheckedChange={setConfirmHistoricalMode}
              />
              <div className="space-y-1">
                <Label 
                  htmlFor="confirm-historical" 
                  className="font-medium cursor-pointer"
                >
                  I understand and want to proceed
                </Label>
                <p className="text-sm text-muted-foreground">
                  I acknowledge that importing historical transactions may require 
                  reconciliation adjustments to avoid double-counting with my opening balances.
                </p>
              </div>
            </div>
          )}
        </div>

        <DialogFooter className="flex flex-col sm:flex-row gap-2">
          <Button 
            variant="outline" 
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </Button>
          
          {!validationResult.can_proceed ? (
            <Button 
              disabled
              className="bg-slate-300"
            >
              Blocked by Policy
            </Button>
          ) : (
            <Button 
              onClick={handleProceed}
              disabled={loading || (validationResult.requires_confirmation && !confirmHistoricalMode)}
              className={hasHighSeverityWarning ? 'bg-red-600 hover:bg-red-700' : 'bg-amber-600 hover:bg-amber-700'}
            >
              {loading ? (
                <>
                  <Clock className="w-4 h-4 mr-2 animate-spin" />
                  Processing...
                </>
              ) : (
                <>
                  Proceed Anyway
                  <ArrowRight className="w-4 h-4 ml-2" />
                </>
              )}
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/**
 * Hook to validate bank feed import safety
 */
export const useBankFeedSafety = () => {
  const [validationResult, setValidationResult] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);

  const validateImport = async (startDate, bankAccountId = null) => {
    try {
      const response = await axios.post(
        `${API}/opening-position/validate-bank-connection`,
        null,
        {
          params: { 
            start_date: startDate,
            bank_name: bankAccountId 
          }
        }
      );

      const result = response.data;
      setValidationResult(result);

      if (!result.can_proceed || result.requires_confirmation) {
        setShowModal(true);
        return false;
      }

      return true;
    } catch (error) {
      console.error('Validation failed:', error);
      // On error, allow proceeding (fail open for better UX)
      return true;
    }
  };

  const checkAndProceed = async (startDate, action, bankAccountId = null) => {
    setPendingAction(() => action);
    
    const canProceed = await validateImport(startDate, bankAccountId);
    
    if (canProceed) {
      await action();
    }
  };

  const handleModalProceed = async () => {
    if (pendingAction) {
      await pendingAction();
    }
    setShowModal(false);
    setPendingAction(null);
  };

  const handleModalClose = () => {
    setShowModal(false);
    setPendingAction(null);
    setValidationResult(null);
  };

  return {
    validateImport,
    checkAndProceed,
    validationResult,
    showModal,
    handleModalProceed,
    handleModalClose,
    BankFeedSafetyModalComponent: (
      <BankFeedSafetyModal
        open={showModal}
        onClose={handleModalClose}
        onProceed={handleModalProceed}
        validationResult={validationResult}
      />
    )
  };
};

export default BankFeedSafetyModal;
